import { useState } from 'react';
import BottomSheet from '../Shared/BottomSheet';
import { useToast } from '../../context/ToastContext';
import fetchWithAuth from '../../utils/fetchWithAuth';

export type ReportModalProps = {
  /** Controls sheet visibility */
  isOpen: boolean;
  /** Called when the sheet should close */
  onClose: () => void;
  /** Called _after_ a successful report, so parent can toast/navigate */
  onSubmit?: (opts: { blockUser: boolean; targetId: string }) => void;
  /** "RECIPE" or "USER" */
  type: 'RECIPE' | 'USER';
  /** ID of the recipe or user being reported */
  targetId: string;
};

const reasonsByType: Record<'RECIPE' | 'USER', string[]> = {
  RECIPE: [
    'Incorrect ingredients',
    'Incorrect instructions',
    'Offensive content',
    'Bad image',
  ],
  USER: [
    'Offensive name',
    'Inappropriate image',
    'Spam recipes',
    'Spam reviews',
    'Harassment'
  ],
};

export default function ReportModal({
  isOpen,
  onClose,
  onSubmit,
  type,
  targetId,
}: ReportModalProps) {
  const [reason, setReason] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [blockUser, setBlockUser] = useState<boolean>(false);
  const { showToast } = useToast() as any;

  const handleSubmit = async () => {
    if (!reason) return;
    try {
      // build the request body
      const body: any = {
        type,
        reason,
        description,
        blockUser,
        ...(type === 'RECIPE'
          ? { recipeId: targetId }
          : { userId: targetId }),
      };

      await fetchWithAuth('/api/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      // notify parent (for toast/undo/navigation)
      onSubmit?.({ blockUser, targetId });
      onClose();
    } catch (err) {
      console.error('Report error', err);
      showToast({ message: 'Failed to submit report', type: 'error' });
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="space-y-4">
        <h2 className="mb-2 text-lg font-semibold flex items-center gap-2">
          Report {type.toLowerCase()}
        </h2>

        <select
          className="w-full border rounded p-2"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">Select a reason</option>
          {reasonsByType[type].map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <textarea
          className="w-full border rounded p-2"
          placeholder="Additional details (optional)"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {/* New block checkbox */}
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={blockUser}
            onChange={() => setBlockUser((b) => !b)}
          />
          <span className="text-sm">Also block this user</span>
        </label>

        <button
          className="w-full py-3 bg-red-600 text-white rounded-full disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!reason}
        >
          Submit Report
        </button>
      </div>
    </BottomSheet>
  );
}